import { RefObject, useCallback, useEffect, useMemo, useState } from "react";
import CustomStore from "devextreme/data/custom_store";
import useApi, { IApiErreur } from "./useApi";
import DataSource from "devextreme/data/data_source";
import IDataSource from "../models/IDataSource";
import Guid from "../models/Guid";
import { AsyncRule } from "devextreme/common";
import { custom } from 'devextreme/ui/dialog';
import { FieldDataChangedEvent } from "devextreme/ui/form";
import { useAuth } from "../contexts/auth";
import IReadListOptions from "../models/IReadListOptions";
import { DataGridRef } from "devextreme-react/data-grid";
import { useContextPopupPage } from "../components/popups/PopupPage";

export interface IFormBaseDataSourceOptions<TReadData = any,> {
  path: string,
  primaryKey: string,
  listColumnsFieldsname?: string[]
  listOption?: IReadListOptions
  customReadAllPath?: string,
  customReadByIdPath?: string,
  customSauvegarderPath?: string,
  includeInactives?: boolean,
  includeReservedIds?: boolean,
  defaultId?: Guid,
  newRow?: boolean
  onSauvegarder?: (data: TReadData) => void,
  isModeSelection?: boolean
}

export enum Mode {
  Ajout,
  Modifier,
  Lecture
}

export interface ICustomRule extends AsyncRule {
  propertyName: string
}

const useCustomDataSource = <TListData, TReadData = TListData,>({
  path,
  primaryKey,
  customReadAllPath,
  customReadByIdPath,
  listColumnsFieldsname,
  customSauvegarderPath,
  includeInactives,
  includeReservedIds,
  defaultId,
  newRow,
  onSauvegarder,
  isModeSelection
}: IFormBaseDataSourceOptions<TReadData>): IDataSource<TListData, TReadData> => {
  const { data: selectedDataRow, getData: getSelectedData, setData: setSelectedData, fetchData } = useApi<TReadData>();
  const [mode, setMode] = useState<Mode>(Mode.Lecture)
  //const [isModeModify, setIsModeModify] = useState<boolean>(false);
  const pkey = primaryKey as keyof TReadData;
  const selectedId: Guid = selectedDataRow && selectedDataRow[pkey] as unknown as Guid || Guid.empty;
  const [isFieldModifier, setIsFieldModifier] = useState(false);
  const [erreurs, setErreurs] = useState<IApiErreur[]>([]);
  const [validationRules, setValidationRules] = useState<ICustomRule[]>([]);
  const [mainFormRef, setMainFormRef] = useState<any>();
  const [gridListRef, setgridListRef] = useState<RefObject<DataGridRef> | undefined>();
  const [modifiedFields, setModifiedFields] = useState<string[]>([])
  const { user } = useAuth();
  const [forceFocusOnSelectedId, setForceFocusOnSelectedId] = useState(false);
  const { isContextInitialized } = useContextPopupPage();
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(newRow || false);

  const beforeUnloadlistener = useCallback((event: any) => {
    if (isFieldModifier || mode === Mode.Modifier || mode === Mode.Ajout) {
      event.preventDefault();
      event.returnValue = 'Les modifications seront perdues. Êtes-vous sûr(e) de vouloir quitter ?';
    }
  }, [isFieldModifier, mode]);

  useEffect(() => {
    window.addEventListener('beforeunload', beforeUnloadlistener);
    return () => {
      window.removeEventListener("beforeunload", beforeUnloadlistener)
    };
  }, [beforeUnloadlistener])

  useEffect(() => {
    if (forceFocusOnSelectedId)
      setForceFocusOnSelectedId(false);
    setErreurs([]);
  }, [selectedId]);

  const dataStore = useMemo(() => {
    return new CustomStore({
      key: primaryKey,
      load: async (options) => {
        if (newRow) return;
        const inclureInactif = (Array.isArray(options.filter) && options.filter.some(s => s.toLowerCase().includes("inactif"))) || false;
        return await fetchData("get", { returnOnly: true, customPath: `${customReadAllPath ? customReadAllPath : `${path}/list?includeInactives=${includeInactives || inclureInactif || false}&includeReservedIds=${includeReservedIds || false}`}${listColumnsFieldsname ? `&fieldsList=${listColumnsFieldsname.join()}` : ''}` });
      },
      byKey: async (key: any) => {
        if (isFirstLoad) {
          setIsFirstLoad(false);
          return;
        }
        if (!key || key.toString?.() === Guid.empty.toString()) {
          return;
        }
        //if (isModeSelection && mode === Mode.Ajout) return;
        return await getSelectedData({ customPath: customReadByIdPath?.replace("{key}", key) || `${path}/${key}` })
        //setSelectedId(result ? result[pkey] as unknown as Guid || Guid.empty : Guid.empty);
      },
    });
  }, [primaryKey, customReadByIdPath, fetchData, listColumnsFieldsname, customReadAllPath, path, includeInactives, includeReservedIds, getSelectedData, isFirstLoad, newRow])

  const dataSource = useMemo(() => {
    return new DataSource({
      store: dataStore,
      reshapeOnPush: true,
    })
  }, [dataStore]);

  useEffect(() => {
    if (!defaultId || defaultId.toString() === Guid.empty.toString()) return;

    dataStore?.byKey(defaultId)
    setForceFocusOnSelectedId(true)
  }, [defaultId, dataStore])

  const validate = useCallback(async (data: TReadData | undefined) => {
    const result = await fetchData("post", {
      customPath: `${path}/ValidateModel`,
      body: data as unknown as object,
    });
    return result as unknown[] as IApiErreur[];
  }, [fetchData, path]);

  const validationCallback = useCallback((e: any) => {
    return new Promise(async (resolve, reject) => {
      if (mode !== Mode.Modifier) return resolve(undefined);
      try {
        const formData = mainFormRef?.option('formData');
        const result: IApiErreur[] = await validate(formData);
        const erreur = result.find(f => f.propertyName.toLowerCase() === e.formItem.dataField.toLowerCase())
        if (erreur) {
          reject(erreur.errorMessage);
        }
      } catch (ex) {
        resolve(undefined);
      }
      resolve(undefined);
    })
  }, [mainFormRef, mode, validate])

  const customizeItem = useCallback((item: any) => {
    if (mode !== Mode.Modifier || !validationRules || validationRules.length === 0 || !item.dataField) return
    // const rule = validationRules.find(f => f.propertyName.toLowerCase() === item.dataField.toLowerCase() || item.dataField.toLowerCase().includes(`.${f.propertyName.toLowerCase()}`))//{type: "async", message: "", validationCallback, reevaluate: true} as unknown as ICustomRule
    const dataField = typeof item.dataField === 'string' ? item.dataField.toLowerCase() : '';
    const rule = validationRules.find(f => {
      const prop = typeof f?.propertyName === 'string' ? f.propertyName.toLowerCase() : '';
      return prop && dataField && (prop === dataField || dataField.includes(`.${prop}`));
    })
    if (rule) {
      rule.validationCallback = validationCallback;
      item.validationRules = [rule];
    }
  }, [validationRules, mode, validationCallback])

  useEffect(() => {
    if (mainFormRef) {
      mainFormRef?.option("customizeItem", customizeItem);
      // mainFormRef.items.forEach((i:any) => console.log(i));
    }
  }, [mainFormRef, validationRules, customizeItem])

  const onFieldDataChanged = useCallback((e: FieldDataChangedEvent) => {
    if (mode === Mode.Lecture) {
      return;
    }

    const updatedField = e.dataField;
    if (updatedField)
      setModifiedFields(prev => [...prev, updatedField])

    setIsFieldModifier(true)
  }, [setIsFieldModifier, mode])

  useEffect(() => {
    fetchData("get", { customPath: `${path}/getValidation` }).then((result: string[]) => {
      if (result)
        setValidationRules(result.map(fieldName => {
          return { type: "async", message: "", reevaluate: true, propertyName: fieldName } as ICustomRule
        }))
    })

  }, [])

  function isValidationErreur(arg: any): arg is IApiErreur[] {
    return arg !== undefined && arg.length > 0 && arg[0] !== undefined && arg[0].propertyName !== undefined && typeof (arg[0].propertyName) === 'string'
  }

  useEffect(() => {
    if (!erreurs || erreurs.length <= 0) return;
    let msgError = "Une ou plusieurs données sont manquantes </br> <ul>";
    erreurs.forEach(err => {
      msgError += `</br> <li>${err.errorMessage}</li>`
      //console.log(err)
    });
    msgError += "</ul>";
    let myDialog = custom({
      dragEnabled: true,
      title: "Erreurs",
      messageHtml: msgError,
      buttons: [{
        text: "OK",
        onClick: (e) => {
          return { buttonText: e.component.option("text") }
        }
      }]
    });
    myDialog.show().then(async (dialogResult: any) => {
      if (dialogResult.buttonText === "OK") {
      }
    });
  }, [erreurs])

  const sauvegarder = useCallback(async (e: any): Promise<boolean> => {
    try {
      const fields = Object.entries(selectedDataRow as unknown as object);
      if (fields) {
        fields.forEach(([field]) => {
          if (field === "idUsagerCreation" && selectedDataRow && (((selectedDataRow as any)[primaryKey])?.toString() === Guid.empty.toString() || !(selectedDataRow as any)[primaryKey])) {
            (selectedDataRow as any)[field] = user?.idUsager as Guid;
          }

          if (field === "idUsagerModification") {
            (selectedDataRow as any)[field] = user?.idUsager as Guid;
          }
        })
      }

      const validationErreurs = await validate(selectedDataRow);
      if (validationErreurs && validationErreurs.length > 0) {
        setErreurs(validationErreurs);
        return false;
      }

      const result = await fetchData("post", {
        customPath: customSauvegarderPath || path,
        body: { selectedDataRow: selectedDataRow as unknown as object, modifiedFields, IdUsager: user?.idUsager }
      });

      if (isValidationErreur(result)) {
        // console.log(result)
        setErreurs(result as IApiErreur[])

        return false;
      }

      //setSelectedId(new Guid(result[primaryKey]))
      setForceFocusOnSelectedId(true)
      dataSource?.reload();
      setMode(Mode.Lecture)
      setIsFieldModifier(false);

      if (onSauvegarder)
        onSauvegarder(result);

      dataStore.byKey(result[primaryKey]);
      return result[primaryKey];
    } catch (exception) {
      return false;
    }
  }, [setErreurs, fetchData, dataSource, customSauvegarderPath, path, selectedDataRow, modifiedFields, primaryKey, user?.idUsager, validate, onSauvegarder, dataStore])


  const effacer = useCallback(async (): Promise<boolean> => {
    let myDialog = custom({
      dragEnabled: true,
      title: "Effacer",
      messageHtml: "Voulez-vous supprimer l'élément sélectionné ?",
      buttons: [{
        text: "Oui",
        onClick: (e) => {
          return { buttonText: e.component.option("text") }
        }
      }, {
        text: "Non",
        onClick: (e) => {
          return { buttonText: e.component.option("text") }
        }
      }]
    });

    myDialog.show().then(async (dialogResult: any) => {
      if (dialogResult.buttonText === "Oui") {
        await fetchData("delete", { customPath: `${path}/${selectedId}` });
        await dataSource?.reload();
      }
    });
    return true;
  }, [fetchData, dataSource, path])

  const annuler = useCallback(async () => {
    let myDialog = custom({
      dragEnabled: true,
      title: "Annuler",
      messageHtml: "Voulez-vous annuler les dernières modifications ?",
      buttons: [{
        text: "Oui",
        onClick: (e) => {
          return { buttonText: e.component.option("text") }
        }
      }, {
        text: "Non",
        onClick: (e) => {
          return { buttonText: e.component.option("text") }
        }
      }]
    });
    const dialogResult = await myDialog.show();
    if (dialogResult.buttonText === "Oui") {
      const selectedRow = gridListRef?.current?.instance();
      if (selectedId !== Guid.empty)
        await dataSource?.store()?.byKey(selectedId); // La condition permet d'éviter que selectId = Guid.empty soit envoyé au backend quand on annule une modification

      setMode(Mode.Lecture);
      setIsFieldModifier(false);

    }
    return true;
  }, [dataSource, gridListRef?.current])

  return {
    dataSource,
    selectedDataRow: selectedDataRow || {} as TReadData,
    erreurs,
    validationRules,
    selectedId: selectedId,
    mode,
    //isModeModifier: isModeModify,
    isFieldModifier,
    setIsFieldModifier,
    //setIsModeModify: modeModify,
    setMode,
    /*toggleModeModify: async () => {
        await dataSource?.store().byKey(selectedId);
        setIsModeModify(prev => !prev);
        setIsFieldModifier(false);

    },*/
    ajouter: async () => {
      setMode(Mode.Ajout);
      var data = await fetchData("get", { customPath: `${path}/default` });
      setSelectedData({ ...data });

      //setIsModeModify(true);
    },
    sauvegarder: sauvegarder,
    effacer: effacer,
    annuler: annuler,
    validate,
    setMainForm: setMainFormRef,
    setGridListForm: setgridListRef,
    onFieldDataChanged,
    forceFocusOnSelectedId,
    setSelectedDataRow: setSelectedData,
  }
}

export default useCustomDataSource;